import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { moviesApi } from "@/api/movies";
import type { CollectionListEntry } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";

/**
 * `/movies/collections` — list of every TMDB collection we know about,
 * with a coverage chip ("X/Y in library") + monitor toggle + "Add all"
 * button per row. Issue #49.
 *
 * The list is sourced from `movies.collection_tmdb_id`-linked rows; if
 * the user hasn't added any movie that belongs to a collection yet,
 * the page is empty. Adding a movie automatically populates the
 * `movie_collections` row via the add-movie handler.
 */
export function MovieCollections() {
  const { data: collections, isLoading } = useQuery({
    queryKey: ["collections"],
    queryFn: moviesApi.collections.list,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Movies"
        title="Collections"
        description="Franchises and series TMDB groups together. Toggle Monitor to auto-add new entries as they're announced."
      />
      {isLoading ? (
        <Skeleton.Cards count={6} cols={3} />
      ) : !collections || collections.length === 0 ? (
        <EmptyState
          glyph="film"
          title="No collections yet"
          description="When you add a movie that's part of a TMDB collection — e.g. The Lord of the Rings, MCU — its collection will show up here."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {collections.map((c) => (
            <CollectionCard key={c.tmdb_id} entry={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function CollectionCard({ entry }: { entry: CollectionListEntry }) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const monitorMut = useMutation({
    mutationFn: (monitored: boolean) =>
      moviesApi.collections.monitor(entry.tmdb_id, monitored),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["collections"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Update failed", "error"),
  });

  const addAllMut = useMutation({
    mutationFn: () => moviesApi.collections.addAll(entry.tmdb_id),
    onSuccess: (r) => {
      toast(
        `Added ${r.added}, skipped ${r.skipped}`,
        r.added > 0 ? "success" : "info",
      );
      qc.invalidateQueries({ queryKey: ["collections"] });
      qc.invalidateQueries({ queryKey: ["movies"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Bulk-add failed", "error"),
  });

  // Coverage display — `total >= in_library` always, but until a
  // refresh has populated `total` they may be equal. Render as
  // "n/m" when known, plain count otherwise.
  const coverage =
    entry.total > entry.in_library
      ? `${entry.in_library} / ${entry.total}`
      : `${entry.in_library}`;

  return (
    <div className="flex gap-3 overflow-hidden rounded-md border border-line bg-surface/30 transition hover:border-line-strong">
      <Link
        to="/movies/collections/$id"
        params={{ id: String(entry.tmdb_id) }}
        className="block shrink-0"
      >
        {entry.poster_url ? (
          <img
            src={entry.poster_url}
            alt=""
            className="aspect-[2/3] h-44 w-28 object-cover"
            loading="lazy"
          />
        ) : (
          <div className="grid aspect-[2/3] h-44 w-28 place-items-center bg-surface-2 text-xs text-foreground/35">
            no poster
          </div>
        )}
      </Link>
      <div className="flex min-w-0 flex-1 flex-col gap-2 p-3">
        <Link
          to="/movies/collections/$id"
          params={{ id: String(entry.tmdb_id) }}
          className="block min-w-0"
        >
          <div className="line-clamp-2 font-display text-base font-medium leading-tight tracking-display-tight">
            {entry.name}
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <Status variant="chip" tone={entry.monitored ? "accent" : "neutral"}>
            {coverage} in library
          </Status>
          {entry.monitored && (
            <Status variant="chip" tone="good">
              monitored
            </Status>
          )}
        </div>
        {entry.overview && (
          <p className="line-clamp-3 text-xs leading-relaxed text-foreground/70">
            {entry.overview}
          </p>
        )}
        <div className="mt-auto flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => monitorMut.mutate(!entry.monitored)}
            loading={monitorMut.isPending}
          >
            {entry.monitored ? "Unmonitor" : "Monitor"}
          </Button>
          <Button
            size="sm"
            onClick={() => addAllMut.mutate()}
            loading={addAllMut.isPending}
          >
            Add all
          </Button>
        </div>
      </div>
    </div>
  );
}
